import {ImmutableStore} from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import {scaleLinear} from 'd3-scale';
import Immutable from 'immutable';
import { startOfDay } from '../utils/Date';
import { parseIntOr, parseJsonIfNeeded } from '../utils/UrlParamParsers';
import Settings from '../utils/Settings';

function defaultState() {
  return {
    data: Immutable.Seq(),
    aggregationLevel: 0,
    sortKey: 'conversationScore',
    ascending: false,
    guideType: 'both',
    customerType: 'both',
    loadingConversationData: false,
    loadingLeaderData: false,
    selectedBranchHierarchy: null,
    branchHierarchy: [],
    branchHierarchyTerms: [],
    conversationData: [],
    conversationProductMix: [],
    productCategoryMap: [],
    viewOption: 'score',
    selectedCategory: '',
    selectedConversation: -1,
    histogramLoaded: false
  };
}

const domains = {
  conversationScore: [0, 100],
  accountScore: [0, 100],
  conversionPercentage: [0, 1],
  leadTransmissionPercentage: [0, 1],
  guideCompletionPercentage: [0, 1],
  accountsOpenedPercentage: [0, 1]
};

let sortKeysTmp = [
  {
    key: 'conversationScore',
    value: 'Efficiency Score'
  }, {
    key: 'guideCompletionPercentage',
    value: 'Guide Completion %'
  }, {
    key: 'guidesCompleted',
    value: 'Guides Completed'
  }, {
    key: 'leadTransmissionPercentage',
    value: 'Contact Entry %'
  }, {
    key: 'leadsTransmitted',
    value: 'Contacts Entered'
  }
];
if (Settings.envFeatures.get('bigBank')) {
  sortKeysTmp = sortKeysTmp.concat([
    {
      key: 'accountScore',
      value: 'Effectiveness Score'
    }, {
      key: 'conversionPercentage',
      value: 'Conversion %'
    }, {
      key: 'converted',
      value: 'Total Conversions'
    }, {
      key: 'accountsOpenedPerClient',
      value: 'Average Opened'
    }, {
      key: 'accountsOpened',
      value: 'Total Products Opened'
    }
  ]);
}
const sortKeys = sortKeysTmp;

function extractCategory(product) {
  const foundCategory = product.get('categories').find(c => c.get('primary'));
  return foundCategory ? foundCategory.get('name') : 'unknown';
}

function countProducts(products, field='count') {
  return products.map(p => p.get(field) || 0).reduce((a, b) => a + b, 0);
}

function buildProductsWithCategory(category, productArray, resultsArray) {
  productArray.forEach((row) => {
    resultsArray[row.get('name')] = category;
  });

  return resultsArray;
}

function setFocused(store, leaderData = Immutable.Seq(), focusedKey) {
  const focusedObj = leaderData.findEntry(leader => {
    return leader.get('key') === focusedKey;
  });

  if (focusedObj) {
    store.focusedObj = focusedObj[1];
    store.focusedIndex = focusedObj[0] + 1;
  } else {
    delete store.focusedObj;
    delete store.focusedIndex;
  }
}

function sortData(data, sortKey, ascending) {
  const sorted = data.sortBy(e => (e.get(sortKey) || 0) * -1).map((e, i) => {
    return e.set('index', i + 1);
  });

  return ascending ? sorted.reverse() : sorted;
}

class SalesLeaderboardStore extends ImmutableStore {
  static storeName = 'SalesLeaderboardStore';

  static handlers = {
    [Actions.SALES_LEADER_DATA_CHANGED]: 'handleSalesLeaderDataChanged',
    [Actions.SALES_LEADER_QUERY_CHANGED]: 'handleSalesLeaderQueryChanged',
    [Actions.CONVERSATION_HISTORY_CHANGED]: 'handleConversationHistoryChanged',
    [Actions.CONVERSATION_PRODUCT_CATEGORY_CHANGED]: 'handleConversationCategoryChanged',
    [Actions.AUTHENTICATION_CHECK_COMPLETED]: 'handleAuth',
    [Actions.AUTHENTICATION_COMPLETED]: 'handleAuth'
  };

  constructor(dispatcher) {
    super(dispatcher);

    const now = startOfDay();
    const before = startOfDay().subtract(1, 'month');

    this.startDate = before.toDate();
    this.endDate = now.toDate();
    this.setState(defaultState());
  }

  handleSalesLeaderQueryChanged(query) {
    const defaults = defaultState();
    const {
      sortKey = defaults.sortKey,
      sortDirection = defaults.sortDirection,
      focused,
      startDate = startOfDay().subtract(1, 'month'),
      endDate = startOfDay(),
      aggregationLevel = defaults.aggregationLevel,
      guideType = defaults.guideType,
      customerType = defaults.customerType,
      selectedBranchHierarchy,
      viewOption,
      selectedCategory,
      selectedConversation = defaults.selectedConversation
    } = query;

    if (startDate || endDate) {
      this.startDate = startDate ? new Date(startDate) : this.startDate;
      this.endDate = endDate ? new Date(endDate) : this.endDate;
      this.emitChange();
    }

    const ascending = sortDirection === 'ascending';
    const data = sortData(this.get('data'), sortKey, ascending);

    const newState = {
      ascending,
      sortKey,
      aggregationLevel: parseIntOr(aggregationLevel, defaults.aggregationLevel),
      guideType,
      customerType,
      data,
      focused,
      selectedBranchHierarchy: parseJsonIfNeeded(selectedBranchHierarchy),
      viewOption,
      selectedCategory,
      selectedConversation
    };
    setFocused(this, data, focused);

    this.mergeState(newState);
  }

  handleSalesLeaderDataChanged(payload) {
    const {leaderData, loading = false} = payload;

    console.log('Updating leaderboard store state', payload);

    const state = {
      loadingLeaderData: loading
    };

    if (leaderData) {
      const sortKey = this.get('sortKey');
      const ascending = this.get('ascending');
      const sortedData = sortData(Immutable.fromJS(leaderData), sortKey, ascending);
      setFocused(this, sortedData, this.get('focused'));
      state.data = sortedData;
    }

    this.mergeState(state);
  }

  handleConversationHistoryChanged(payload) {
    const {conversationData, conversationProductMix, loading = false} = payload;
    let productMixChartData = Immutable.Seq();
    let productsWithCategoryData = {};

    console.log('Updating conversation history store state', payload);

    const state = {
      loadingConversationData: loading
    };

    if (conversationData) {
      state.conversationData = conversationData;
    }

    if (conversationProductMix) {
      const categories = Immutable.fromJS(conversationProductMix.recommendedProducts).groupBy(extractCategory);
      productMixChartData = categories.map((products, category) => {
        const numberOfRecommendations = countProducts(products);
        const numberOfOpens = countProducts(products, 'opened');
        productsWithCategoryData = buildProductsWithCategory(category, products, productsWithCategoryData);

        return {
          productArray: products.toJS(),
          numberOfRecommendations,
          numberOfOpens
        };
      });

      state.conversationProductMix = productMixChartData;
      state.productCategoryMap = productsWithCategoryData;
    }

    this.mergeState(state);
  }

  handleConversationCategoryChanged(selection) {
    const state = {};

    console.log('Updating leaderboard conversation product category selection: ', selection);

    if (selection) {
      state.selectedCategory = selection;
    }

    this.mergeState(state);
  }


  getLeaderData() {
    const sortKey = this.get('sortKey');
    const sorted = this.get('data');
    const scale = scaleLinear();
    let domain = domains[sortKey];

    if (!domain) {
      const values = sorted.map(e => e.get(sortKey));
      let min = 0, max = 1;
      if (values.length) {
        min = values.min();
        max = values.max();
      }

      domain = [min, max];
    }

    const leaderScale = scale.domain(domain).nice().range([0, 100]).clamp(true);

    return {
      leaderData: sorted,
      leaderScale
    };
  }

  isAscendingSort() {
    return this.get('ascending');
  }

  getFocusedLeader() {
    return this.focusedObj;
  }

  getConversationData() {
    return this.get('conversationData').toJS();
  }

  getConversationProductMix() {
    return this.get('conversationProductMix').toJS();
  }

  getProductsRecommendedWithCategoryData() {
    return this.get('productCategoryMap').toJS();
  }

  getFocusedIndex() {
    return this.focusedIndex || 0;
  }

  getLeaderCount() {
    const data = this.get('data');

    return data ? data.size : 0;
  }

  getSortKey() {
    return this.get('sortKey');
  }

  // noinspection JSMethodCanBeStatic
  getSortKeys() {
    return sortKeys;
  }

  getGuideType() {
    return this.get('guideType');
  }

  getCustomerType() {
    return this.get('customerType');
  }

  getSelectedDateRange() {
    return {
      start: this.startDate,
      end: this.endDate
    };
  }

  getAggregationLevel() {
    return this.get('aggregationLevel');
  }

  getViewOption() {
    return this.get('viewOption');
  }

  getSelectedCategory() {
    return this.get('selectedCategory');
  }

  getSelectedConversation() {
    return parseInt(this.get('selectedConversation'));
  }

  isLoading() {
    return this.get('loadingConversationData') || this.get('loadingLeaderData');
  }

  handleAuth = ({ clientInfo: { branchHierarchyTerms, branchHierarchy } = {} } = {}) => {
    if (branchHierarchyTerms && branchHierarchy) {
      this.mergeState({
        branchHierarchy,
        branchHierarchyTerms
      });
    }
  };

  getBranchHierarchy = () => {
    return this.get('branchHierarchy');
  };

  getBranchHierarchyTerms = () => {
    return this.get('branchHierarchyTerms');
  };

  getSelectedBranchHierarchy = () => {
    return this.get('selectedBranchHierarchy');
  };

  rehydrate(state) {
    super.rehydrate(state._state);

    this.startDate = new Date(state.startDate);
    this.endDate = new Date(state.endDate);
    this.focusedObj = Immutable.fromJS(state.focusedObj);
    this.focusedIndex = state.focusedIndex;
  }

  dehydrate() {
    return {
      _state: this._state,
      startDate: this.startDate,
      endDate: this.endDate,
      focusedObj: this.focusedObj,
      focusedIndex: this.focusedIndex
    };
  }
}

export default SalesLeaderboardStore;



// WEBPACK FOOTER //
// ./src/stores/SalesLeaderboardStore.js